import type { ElementType, ReactNode } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton } from 'react-aria-components';
import { cn } from '~/lib/utils';
import Icon, { type IconName } from './Icon';

type SettingsRowProps = Omit<AriaButtonProps, 'children' | 'className'> & {
  /** Element to render as. Defaults to react-aria Button so the row works inside DialogTrigger. */
  as?: ElementType;
  href?: string;
  className?: string;
  icon: IconName;
  label: ReactNode;
  value?: ReactNode;
  /** 'stacked' shows label above value; 'split' shows label left, value right. */
  layout?: 'stacked' | 'split';
  /** Danger styling (destructive label, danger icon backplate). */
  danger?: boolean;
  /** Renders as a standalone card instead of a row inside a group (no divider). */
  standalone?: boolean;
  /** Set to false to render the group divider below the row (rows inside CardBox). */
  isLast?: boolean;
  /** Optional trailing content before the chevron. */
  children?: ReactNode;
};

export default function SettingsRow({
  as: Tag = AriaButton,
  icon,
  label,
  value,
  layout = 'stacked',
  danger = false,
  isLast = true,
  standalone = false,
  className,
  children,
  ...props
}: SettingsRowProps) {
  return (
    <Tag
      className={cn(
        'flex w-full items-center gap-4 text-left outline-none transition-colors hover:bg-row-hover focus-visible:bg-row-hover compact:min-h-17 compact:px-3.5 compact:py-3 compact:gap-3',
        standalone
          ? 'min-h-24 rounded-2xl border border-card-border bg-card-background px-5 py-4'
          : 'min-h-20 px-5 py-5 desktop:px-6',
        !standalone && !isLast && 'border-b border-card-border',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-full compact:size-10',
          danger ? 'bg-danger-icon-background' : 'bg-icon-background',
        )}
      >
        <Icon
          name={icon}
          className={cn(
            'text-[23px] compact:text-[21px]',
            danger ? 'text-destruction' : 'text-icon',
          )}
        />
      </div>
      {layout === 'split' ? (
        <div className="flex min-w-0 flex-1 items-center justify-between gap-3 desktop:gap-6 compact:flex-col compact:items-start compact:gap-1">
          <p className="min-w-0 text-[15px] leading-[1.6] font-normal text-setting-label compact:text-[13px] compact:leading-normal">
            {label}
          </p>
          {value !== undefined && (
            <p className="ml-auto max-w-1/2 desktop:max-w-3/5 truncate text-right text-[15px] leading-[1.6] text-gray-3 compact:ml-0 compact:max-w-full compact:text-left compact:leading-normal compact:whitespace-normal compact:overflow-visible compact:wrap-anywhere">
              {value}
            </p>
          )}
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col gap-2 compact:gap-1">
          <p
            className={cn(
              'text-sm compact:text-[15px]',
              danger ? 'font-normal text-destruction' : 'text-setting-label',
            )}
          >
            {label}
          </p>
          {value !== undefined && (
            <p className="text-[13px] leading-[1.8] text-gray-3 compact:leading-normal">{value}</p>
          )}
        </div>
      )}
      {children}
      <Icon name="chevron_right" className="text-[19px] text-chevron" />
    </Tag>
  );
}
